import React, { useEffect, useMemo, useReducer, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { articlesAPI } from "../services/api";
import { CATEGORIES } from "../lib/categories";
import ArticleCard from "../components/ArticleCard";
import Seo from "../components/Seo";
import "./News.css";

const initialFetch = { data: { items: [], total: 0, total_pages: 1, page: 1 }, loading: true };
function fetchReducer(state, action) {
  switch (action.type) {
    case "loading": return { ...state, loading: true };
    case "ok": return { data: action.data, loading: false };
    case "err": return { ...state, loading: false };
    default: return state;
  }
}

export default function News() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "all";
  const search = params.get("q") || "";
  const page = parseInt(params.get("page") || "1", 10);

  const [{ data, loading }, dispatch] = useReducer(fetchReducer, initialFetch);
  const [searchInput, setSearchInput] = useState(search);

  useEffect(() => {
    let cancelled = false;
    dispatch({ type: "loading" });
    articlesAPI
      .list({
        category: category === "all" ? undefined : category,
        search: search || undefined,
        page,
        page_size: 12,
      })
      .then((r) => { if (!cancelled) dispatch({ type: "ok", data: r.data }); })
      .catch(() => { if (!cancelled) dispatch({ type: "err" }); });
    return () => { cancelled = true; };
  }, [category, search, page]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (!value || value === "all") next.delete(key);
    else next.set(key, value);
    next.delete("page");
    setParams(next);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    updateParam("q", searchInput.trim());
  };

  const pageHeading = useMemo(() => {
    if (category !== "all") {
      const c = CATEGORIES.find((x) => x.slug === category);
      return c ? c.name : "News";
    }
    return "News";
  }, [category]);

  return (
    <div className="news-page" data-testid="news-page">
      <Seo
        title={search ? `Search: ${search}` : `${pageHeading} — Latest Motorsport News`}
        description={`Latest ${pageHeading} news, analysis and opinion from Race377.`}
      />

      <section className="news-hero">
        <div className="container">
          <span className="caption news-hero__eyebrow">Race377 / News</span>
          <h1 className="brand-display news-hero__title">{pageHeading}</h1>
          <p className="body-large news-hero__sub">
            Latest stories, sorted newest first. Filter by series or search the archive.
          </p>
        </div>
      </section>

      <section className="container news-controls">
        <form className="news-search" onSubmit={submitSearch}>
          <Search size={18} />
          <input
            type="search"
            placeholder="Search articles…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            data-testid="news-search-input"
          />
          <button type="submit" className="btn-primary news-search__btn" data-testid="news-search-submit">Search</button>
        </form>

        <div className="news-filters" data-testid="news-filters">
          <button
            type="button"
            className={`news-filter ${category === "all" ? "is-active" : ""}`}
            onClick={() => updateParam("category", "all")}
            data-testid="news-filter-all"
          >
            All
          </button>
          {CATEGORIES.map((c) => (
            <button
              key={c.slug}
              type="button"
              className={`news-filter ${category === c.slug ? "is-active" : ""}`}
              onClick={() => updateParam("category", c.slug)}
              data-testid={`news-filter-${c.slug}`}
            >
              {c.name}
            </button>
          ))}
        </div>
      </section>

      <section className="container news-results">
        {loading ? (
          <div className="loading-container"><div className="loading-spinner" /></div>
        ) : data.items.length === 0 ? (
          <p className="body-medium" style={{ padding: "60px 0", color: "var(--text-secondary)" }}>
            No articles found{search ? ` for "${search}"` : ""}.
          </p>
        ) : (
          <>
            <div className="news-grid" data-testid="news-grid">
              {data.items.map((a) => <ArticleCard key={a.id} article={a} />)}
            </div>
            {data.total_pages > 1 && (
              <div className="news-pagination">
                <button
                  className="btn-secondary"
                  disabled={page <= 1}
                  onClick={() => { const n = new URLSearchParams(params); n.set("page", String(page - 1)); setParams(n); }}
                >
                  Previous
                </button>
                <span className="caption">Page {data.page} of {data.total_pages}</span>
                <button
                  className="btn-secondary"
                  disabled={page >= data.total_pages}
                  onClick={() => { const n = new URLSearchParams(params); n.set("page", String(page + 1)); setParams(n); }}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
